/**
 * Viewer Tracking System
 * Tracks real-time active viewers, unique visitors, and song interactions
 */

import { db } from './firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  increment, 
  serverTimestamp,
  onSnapshot,
  query,
  where,
  getDocs,
  getDoc
} from 'firebase/firestore';

/**
 * Generate a unique visitor ID (stored in localStorage)
 */
const getVisitorId = () => {
  let visitorId = localStorage.getItem('ysr_visitor_id');
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ysr_visitor_id', visitorId);
  }
  return visitorId;
};

/**
 * Resolve current live session_id for an artist (public catalogue).
 * Used when SongCard mounts before live_status has propagated sessionId.
 */
export async function resolveArtistLiveSessionId(artistId) {
  if (!artistId) return null;
  try {
    const snap = await getDoc(doc(db, 'live_status', artistId));
    if (!snap.exists()) return null;
    const d = snap.data();
    if (d.is_live && d.session_id) return d.session_id;
    return null;
  } catch (e) {
    console.warn('resolveArtistLiveSessionId failed:', e?.message || e);
    return null;
  }
}

/**
 * Extra page_visits row scoped to the current paid/live session (does not duplicate daily_stats).
 * Call when live_status becomes available so Admin funnel counts visits for this session.
 */
export async function recordLiveSessionPageVisit(artistId, artistUrlSlug, liveSessionId) {
  if (!artistId || !artistUrlSlug || !liveSessionId) return;
  try {
    const visitorId = getVisitorId();
    const today = new Date().toISOString().split('T')[0];
    const visitRef = doc(collection(db, 'page_visits'));
    await setDoc(visitRef, {
      artist_id: artistId,
      artist_url_slug: artistUrlSlug,
      visitor_id: visitorId,
      session_id: liveSessionId,
      timestamp: serverTimestamp(),
      date: today,
    });
  } catch (e) {
    console.warn('recordLiveSessionPageVisit (non-critical):', e?.message || e);
  }
}

/**
 * Track a page view for an artist
 * Creates/updates active viewer and records visit
 */
export const trackPageView = async (artistId, artistUrlSlug) => {
  if (!artistId || !artistUrlSlug) {
    console.warn('trackPageView: Missing artistId or artistUrlSlug', { artistId, artistUrlSlug });
    return;
  }

  try {
    const visitorId = getVisitorId();
    const today = new Date().toISOString().split('T')[0];
    
    // Track active viewer (real-time) - CRITICAL: This must succeed
    const activeViewerRef = doc(db, 'active_viewers', `${artistId}_${visitorId}`);
    await setDoc(activeViewerRef, {
      artist_id: artistId,
      artist_url_slug: artistUrlSlug,
      visitor_id: visitorId,
      last_seen: serverTimestamp(),
      first_seen: serverTimestamp(),
      session_start: serverTimestamp()
    }, { merge: true });
    console.log('✅ Active viewer tracked:', { artistId, visitorId });

    // Record visit in analytics - Non-critical, but important
    try {
      const visitRef = doc(collection(db, 'page_visits'));
      await setDoc(visitRef, {
        artist_id: artistId,
        artist_url_slug: artistUrlSlug,
        visitor_id: visitorId,
        timestamp: serverTimestamp(),
        date: today
      });
    } catch (visitError) {
      console.error('Error recording visit (non-critical):', visitError);
      // Continue even if visit recording fails
    }

    // Update daily stats - Non-critical, but important
    try {
      const dailyStatsRef = doc(db, 'artist_daily_stats', `${artistId}_${today}`);
      await setDoc(dailyStatsRef, {
        artist_id: artistId,
        date: today,
        total_visits: increment(1),
        last_updated: serverTimestamp()
      }, { merge: true });

      // Check if this is a unique visitor for today
      const uniqueVisitorsQuery = query(
        collection(db, 'page_visits'),
        where('artist_id', '==', artistId),
        where('date', '==', today)
      );
      const visitsSnapshot = await getDocs(uniqueVisitorsQuery);
      const uniqueVisitorIds = new Set(visitsSnapshot.docs.map(d => d.data().visitor_id));
      
      if (!uniqueVisitorIds.has(visitorId)) {
        // This is a new unique visitor for today
        await updateDoc(dailyStatsRef, {
          unique_visitors: increment(1)
        });
      }
    } catch (statsError) {
      console.error('Error updating daily stats (non-critical):', statsError);
      // Continue even if stats update fails
    }

  } catch (error) {
    // CRITICAL ERROR - Log but don't throw to prevent breaking the page
    console.error('❌ CRITICAL: Error tracking page view:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      artistId,
      artistUrlSlug
    });
  }
};

/**
 * Track song interaction (viewed, added to cart, paid)
 */
export const trackSongInteraction = async (artistId, songId, interactionType, sessionId = null) => {
  if (!artistId || !songId || !interactionType) {
    console.warn('trackSongInteraction: Missing required params', { artistId, songId, interactionType });
    return;
  }

  try {
    const visitorId = getVisitorId();
    const today = new Date().toISOString().split('T')[0];
    
    // Record interaction - Non-critical but important
    try {
      const interactionRef = doc(collection(db, 'song_interactions'));
      await setDoc(interactionRef, {
        artist_id: artistId,
        song_id: songId,
        visitor_id: visitorId,
        interaction_type: interactionType, // 'viewed', 'added_to_cart', 'paid'
        session_id: sessionId,
        timestamp: serverTimestamp(),
        date: today
      });
    } catch (interactionError) {
      console.error('Error recording song interaction (non-critical):', interactionError);
      // Continue even if interaction recording fails
    }

    // Update song stats - Non-critical but important
    try {
      const songStatsRef = doc(db, 'song_daily_stats', `${songId}_${today}`);
      const updateData = {
        song_id: songId,
        artist_id: artistId,
        date: today,
        last_updated: serverTimestamp()
      };

      if (interactionType === 'viewed') {
        updateData.times_viewed = increment(1);
      } else if (interactionType === 'added_to_cart') {
        updateData.times_added_to_cart = increment(1);
      } else if (interactionType === 'paid') {
        updateData.times_paid = increment(1);
      }

      await setDoc(songStatsRef, updateData, { merge: true });
    } catch (statsError) {
      console.error('Error updating song stats (non-critical):', statsError);
      // Continue even if stats update fails
    }

  } catch (error) {
    // Log but don't throw - tracking should be non-blocking
    console.error('Error tracking song interaction:', error);
  }
};

/**
 * Get real-time active viewer count for an artist
 * Returns a function to unsubscribe
 */
export const subscribeToActiveViewers = (artistId, callback) => {
  if (!artistId) return () => {};

  // Query all active viewers for this artist
  const activeViewersQuery = query(
    collection(db, 'active_viewers'),
    where('artist_id', '==', artistId)
  );

  return onSnapshot(activeViewersQuery, (snapshot) => {
    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000); // 5 minutes threshold
    
    // Filter out viewers who haven't been seen in the last 5 minutes
    const activeViewers = snapshot.docs.filter(doc => {
      const data = doc.data();
      const lastSeen = data.last_seen?.toDate?.()?.getTime() || data.last_seen || 0;
      return lastSeen > fiveMinutesAgo;
    });

    callback(activeViewers.length);
  }, (error) => {
    console.error('Error subscribing to active viewers:', error);
    callback(0);
  });
};

/**
 * Update last seen timestamp for active viewer
 * Should be called periodically (e.g., every 30 seconds) while user is on page
 * Uses setDoc with merge to ensure document exists even if trackPageView hasn't completed
 */
export const updateLastSeen = async (artistId) => {
  if (!artistId) return;

  try {
    const visitorId = getVisitorId();
    const activeViewerRef = doc(db, 'active_viewers', `${artistId}_${visitorId}`);
    // Use setDoc with merge to create if doesn't exist, update if it does
    await setDoc(activeViewerRef, {
      artist_id: artistId,
      visitor_id: visitorId,
      last_seen: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    // Log error but don't throw - tracking should be non-blocking
    console.error('Error updating last seen:', error);
    // Try to create document if update failed
    try {
      const visitorId = getVisitorId();
      const activeViewerRef = doc(db, 'active_viewers', `${artistId}_${visitorId}`);
      await setDoc(activeViewerRef, {
        artist_id: artistId,
        visitor_id: visitorId,
        last_seen: serverTimestamp(),
        first_seen: serverTimestamp()
      });
    } catch (createError) {
      console.error('Error creating active viewer document:', createError);
    }
  }
};

/**
 * Clean up active viewer when user leaves page
 */
export const removeActiveViewer = async (artistId) => {
  if (!artistId) return;

  try {
    const visitorId = getVisitorId();
    const activeViewerRef = doc(db, 'active_viewers', `${artistId}_${visitorId}`);
    await updateDoc(activeViewerRef, {
      last_seen: serverTimestamp(),
      session_end: serverTimestamp()
    });
    // Note: We don't delete, just mark as inactive. Cleanup can happen via Cloud Function.
  } catch (error) {
    console.error('Error removing active viewer:', error);
  }
};

/**
 * Get historical stats for an artist
 */
export const getArtistStats = async (artistId, startDate = null, endDate = null) => {
  if (!artistId) return null;

  try {
    const statsQuery = query(
      collection(db, 'artist_daily_stats'),
      where('artist_id', '==', artistId)
    );
    
    const snapshot = await getDocs(statsQuery);
    const stats = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter by date range if provided
    let filteredStats = stats;
    if (startDate) {
      filteredStats = filteredStats.filter(s => s.date >= startDate);
    }
    if (endDate) {
      filteredStats = filteredStats.filter(s => s.date <= endDate);
    }

    // Aggregate totals
    const totals = filteredStats.reduce((acc, stat) => {
      acc.total_visits += stat.total_visits || 0;
      acc.unique_visitors += stat.unique_visitors || 0;
      return acc;
    }, { total_visits: 0, unique_visitors: 0 });

    return {
      daily: filteredStats,
      totals
    };
  } catch (error) {
    console.error('Error getting artist stats:', error);
    return null;
  }
};

/**
 * Get song interaction stats for an artist
 */
export const getSongInteractionStats = async (artistId, startDate = null, endDate = null) => {
  if (!artistId) return null;

  try {
    const interactionsQuery = query(
      collection(db, 'song_interactions'),
      where('artist_id', '==', artistId)
    );
    
    const snapshot = await getDocs(interactionsQuery);
    let interactions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter by date range if provided
    if (startDate) {
      interactions = interactions.filter(i => i.date >= startDate);
    }
    if (endDate) {
      interactions = interactions.filter(i => i.date <= endDate);
    }

    // Aggregate by interaction type
    const stats = {
      viewed: 0,
      added_to_cart: 0,
      paid: 0,
      by_song: {}
    };

    interactions.forEach(interaction => {
      const type = interaction.interaction_type;
      if (type === 'viewed') stats.viewed++;
      else if (type === 'added_to_cart') stats.added_to_cart++;
      else if (type === 'paid') stats.paid++;

      // Group by song
      const songId = interaction.song_id;
      if (!stats.by_song[songId]) {
        stats.by_song[songId] = { viewed: 0, added_to_cart: 0, paid: 0 };
      }
      if (type === 'viewed') stats.by_song[songId].viewed++;
      else if (type === 'added_to_cart') stats.by_song[songId].added_to_cart++;
      else if (type === 'paid') stats.by_song[songId].paid++;
    });

    return stats;
  } catch (error) {
    console.error('Error getting song interaction stats:', error);
    return null;
  }
};

